<script>
  export let model = null;
  export let side = "before";
  export let compareModel = null;
  export let showHeader = true;

  let open = true;
  let afterValueMode = "absolute";
  const metrics = [
    { key: "avgTimeSeconds", label: "Avg Time", duration: true },
    { key: "activities", label: "Activities" },
    { key: "apps", label: "Apps" },
  ];
  const modelStatsCache = new Map();
  $: stats = getModelStats(model);
  $: compareStats = getModelStats(compareModel);
  $: headerLabel = side === "after" || side === "summary" ? "Changes" : "Baseline";

  function getStoredAvgTime(currentModel) {
    const directValue = Number(currentModel?.avgTimeSeconds);
    if (
      currentModel?.avgTimeSeconds !== null &&
      currentModel?.avgTimeSeconds !== undefined &&
      Number.isFinite(directValue)
    ) {
      return directValue;
    }

    const activityMetrics = Array.isArray(currentModel?.activityMetrics)
      ? currentModel.activityMetrics
      : [];
    if (!activityMetrics.length) return null;
    return activityMetrics.reduce((total, metric) => {
      const weightedTime = Number(metric?.weighted_time_seconds);
      if (Number.isFinite(weightedTime)) return total + weightedTime;
      const avgTime = Number(metric?.avg_time_seconds);
      const frequency = Number(metric?.frequency);
      return Number.isFinite(avgTime) && Number.isFinite(frequency)
        ? total + avgTime * frequency
        : total;
    }, 0);
  }

  function getStoredActivityCount(currentModel) {
    const directValue = Number(
      currentModel?.activitiesCount ??
        currentModel?.activities_count ??
        currentModel?.activities,
    );
    if (Number.isFinite(directValue)) return directValue;

    const activityMetrics = Array.isArray(currentModel?.activityMetrics)
      ? currentModel.activityMetrics
      : [];
    return activityMetrics.length || null;
  }

  function getStoredAppCount(currentModel) {
    const directValue = Number(
      currentModel?.appsCount ??
        currentModel?.apps_count ??
        currentModel?.apps,
    );
    if (Number.isFinite(directValue)) return directValue;

    const apps = new Set();
    const activityMetrics = Array.isArray(currentModel?.activityMetrics)
      ? currentModel.activityMetrics
      : [];
    activityMetrics.forEach((metric) => {
      const metricApps = metric?.apps;
      if (Array.isArray(metricApps)) {
        metricApps.forEach((app) => apps.add(app?.name || app?.application || app));
      } else if (metricApps && typeof metricApps === "object") {
        Object.keys(metricApps).forEach((app) => apps.add(app));
      }
    });
    return apps.size || null;
  }

  function getCachedStatsKey(currentModel) {
    return currentModel?.id || currentModel?.name || currentModel?.bpmnXml;
  }

  function getModelStats(currentModel) {
    if (!currentModel) return null;

    const cachedStatsKey = getCachedStatsKey(currentModel);
    if (cachedStatsKey && modelStatsCache.has(cachedStatsKey)) {
      return modelStatsCache.get(cachedStatsKey);
    }

    let avgTimeSeconds = getStoredAvgTime(currentModel);
    let activities = getStoredActivityCount(currentModel);
    let apps = getStoredAppCount(currentModel);

    if (
      (activities !== null && apps !== null && avgTimeSeconds !== null) ||
      typeof DOMParser === "undefined" ||
      !currentModel?.bpmnXml
    ) {
      const stats = { avgTimeSeconds, activities, apps };
      if (cachedStatsKey) modelStatsCache.set(cachedStatsKey, stats);
      return stats;
    }

    const document = new DOMParser().parseFromString(
      currentModel.bpmnXml,
      "application/xml",
    );
    const elements = Array.from(document.getElementsByTagName("*"));
    const activityTypes = new Set([
      "businessRuleTask",
      "callActivity",
      "manualTask",
      "receiveTask",
      "scriptTask",
      "sendTask",
      "serviceTask",
      "subProcess",
      "task",
      "userTask",
    ]);
    const parsedApps = new Set(
      elements
        .filter((element) => element.localName === "dataStoreReference")
        .map((element) => element.getAttribute("name"))
        .filter(Boolean),
    );
    if (avgTimeSeconds === null) {
      const annotations = new Map(
        elements
          .filter((element) => element.localName === "textAnnotation")
          .map((element) => [
            element.getAttribute("id"),
            element.textContent || "",
          ]),
      );
      const weightedTimes = elements
        .filter((element) => element.localName === "association")
        .map((association) =>
          annotations.get(association.getAttribute("targetRef")),
        )
        .filter(Boolean)
        .map((text) => {
          const time = text.match(
            /Avg\.\s*Time:\s*(?:(\d+)\s*min)?\s*(?:(\d+)\s*sec)?/i,
          );
          if (!time || (!time[1] && !time[2])) return NaN;
          const frequency = text.match(/Frequency:\s*(\d+(?:\.\d+)?)%/i);
          const seconds =
            Number(time[1] || 0) * 60 + Number(time[2] || 0);
          return seconds * (Number(frequency?.[1] ?? 100) / 100);
        })
        .filter(Number.isFinite);
      avgTimeSeconds = weightedTimes.length
        ? weightedTimes.reduce((total, value) => total + value, 0)
        : NaN;
    }

    const stats = {
      avgTimeSeconds,
      activities:
        activities ??
        elements.filter((element) => activityTypes.has(element.localName))
          .length,
      apps: apps ?? parsedApps.size,
    };
    if (cachedStatsKey) modelStatsCache.set(cachedStatsKey, stats);
    return stats;
  }

  function getDelta(key) {
    if (side === "before" || !stats || !compareStats) return null;
    const value = Number(stats[key]);
    const compareValue = Number(compareStats[key]);
    if (!Number.isFinite(value) || !Number.isFinite(compareValue)) return null;
    return value - compareValue;
  }

  function getDeltaClass(key) {
    const delta = getDelta(key);
    if (delta === null || delta === 0) return "";
    return delta < 0 ? "down" : "up";
  }

  function hasChanged(metric) {
    const delta = getDelta(metric.key);
    if (delta === null) return false;
    return metric.duration ? Math.round(delta) !== 0 : delta !== 0;
  }

  function formatDuration(value, signed = false) {
    const rounded = Math.round(Math.abs(value));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    const prefix = signed ? (value > 0 ? "+" : value < 0 ? "-" : "") : "";
    if (minutes && seconds) return `${prefix}${minutes}m ${seconds}s`;
    if (minutes) return `${prefix}${minutes}m`;
    return `${prefix}${seconds}s`;
  }

  function formatValue(metric) {
    const value = Number(stats?.[metric.key]);
    if (!Number.isFinite(value)) return "-";
    return metric.duration ? formatDuration(value) : value;
  }

  function formatDelta(metric) {
    const delta = getDelta(metric.key);
    if (delta === null) return "";
    if (!hasChanged(metric)) return metric.duration ? "0s" : "0";
    if (metric.duration) return formatDuration(delta, true);
    return `${delta > 0 ? "+" : ""}${delta}`;
  }

  function formatMetric(metric, mode = afterValueMode) {
    if (side === "after" && mode === "delta") {
      return formatDelta(metric) || "-";
    }
    return formatValue(metric);
  }

  function formatCompareValue(metric) {
    const value = Number(compareStats?.[metric.key]);
    if (!Number.isFinite(value)) return "-";
    return metric.duration ? formatDuration(value) : value;
  }

  function getDeltaTooltip(metric) {
    const delta = formatDelta(metric);
    const baseline = formatCompareValue(metric);
    if (!delta || !hasChanged(metric)) return "";
    return `Changed by ${delta} from ${baseline}`;
  }

  function getMetricLabel(metric) {
    return side === "summary" && metric.duration ? "Time" : metric.label;
  }

  function formatSummaryDuration(value, signed = false) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    const rounded = Math.round(Math.abs(numericValue));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    const prefix = signed
      ? numericValue > 0
        ? "+"
        : numericValue < 0
          ? "-"
          : ""
      : "";
    return `${prefix}${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} min`;
  }

  function formatSummaryValue(metric, values) {
    const value = Number(values?.[metric.key]);
    if (!Number.isFinite(value)) return "-";
    return metric.duration ? formatSummaryDuration(value) : value;
  }

  function formatSummaryDelta(metric) {
    const delta = getDelta(metric.key);
    if (delta === null) return "-";
    if (metric.duration) return formatSummaryDuration(delta, true);
    return `${delta > 0 ? "+" : ""}${delta}`;
  }
</script>

{#if stats}
  <section
    class="quantification_card"
    class:after_card={side === "after"}
    class:summary_card={side === "summary"}
  >
    {#if showHeader}
      <div class="info_header">
        <div class="header_left">
          <button class="header_toggle" type="button" on:click={() => (open = !open)}>
            <span>{headerLabel}</span>
          </button>
          {#if side === "after"}
            <span class="value_mode_toggle">
              <button
                type="button"
                class:active={afterValueMode === "absolute"}
                on:click={() => (afterValueMode = "absolute")}
              >
                Values
              </button>
              <button
                type="button"
                class:active={afterValueMode === "delta"}
                on:click={() => (afterValueMode = "delta")}
              >
                Delta
              </button>
            </span>
          {/if}
        </div>
        <button class="arrow_button" type="button" on:click={() => (open = !open)}>
          <svg
            class="arrow"
            height="0.5rem"
            viewBox="0 0 17 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {#if open}
              <path
                d="M8.5 9.36707e-05L17 8.50009L16.11 9.39014L8.5 1.77093L0.890041 9.38089L7.86186e-08 8.49084L8.5 9.36707e-05Z"
                fill="currentColor"
              />
            {:else}
              <path
                d="M8.5 9.39004L0 0.890042L0.890045 0L8.5 7.6192L16.11 0.00924664L17 0.899292L8.5 9.39004Z"
                fill="currentColor"
              />
            {/if}
          </svg>
        </button>
      </div>
    {/if}
    {#if open}
      {#if side === "summary"}
        <div class="summary_head">
          <span>Baseline</span>
          <span>Comparison</span>
          <span>Delta</span>
        </div>
        <div class="summary_grid">
          {#each metrics as metric}
            <div>
              <small>{getMetricLabel(metric)}</small>
              <strong>{formatSummaryValue(metric, compareStats)}</strong>
            </div>
            <div>
              <small>{getMetricLabel(metric)}</small>
              <strong>{formatSummaryValue(metric, stats)}</strong>
            </div>
            <div class={getDeltaClass(metric.key)}>
              <small>{getMetricLabel(metric)}</small>
              <strong class="metric_value">{formatSummaryDelta(metric)}</strong>
            </div>
          {/each}
        </div>
      {:else}
        <div class="quant_summary">
          {#each metrics as metric}
            <div class={getDeltaClass(metric.key)}>
              <strong>
                <span
                  class:metric_value={side === "after"}
                  aria-label={side === "after" && hasChanged(metric) ? getDeltaTooltip(metric) : null}
                  title={side === "after" && hasChanged(metric) ? getDeltaTooltip(metric) : null}
                >
                  {formatMetric(metric, afterValueMode)}
                </span>
              </strong>
              <span>{metric.label}</span>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </section>
{/if}

<style>
  .quantification_card {
    background: rgba(255, 255, 255, 0.96);
    border: var(--bw) solid var(--pax-black);
    border-radius: 0.5rem;
    overflow: hidden;
    width: 18rem;
  }

  .quantification_card.summary_card {
    border: 0;
    border-radius: 0;
    box-sizing: border-box;
    max-width: none;
    overflow: visible;
    width: 100%;
  }

  .info_header {
    align-items: center;
    display: flex;
    height: 2.5rem;
    justify-content: space-between;
    border-bottom: 1px solid #e8e8e8;
    background: transparent;
    color: var(--pax-black);
    padding: 0 0.5rem 0 0.75rem;
  }

  .header_left {
    align-items: center;
    display: flex;
    gap: 0.4rem;
    min-width: 0;
  }

  .header_toggle,
  .arrow_button {
    align-items: center;
    background: transparent;
    border: 0;
    color: var(--pax-black);
    cursor: pointer;
    display: flex;
    font-size: 0.875rem;
    font-weight: 400;
    height: 100%;
    padding: 0;
    text-align: left;
  }

  .header_toggle {
    flex: 0 0 auto;
    min-width: 0;
  }

  .arrow_button {
    flex: 0 0 auto;
    justify-content: center;
    width: 1.4rem;
  }

  .value_mode_toggle {
    align-items: center;
    background: var(--pax-white);
    border: 1px solid var(--pax-black);
    border-radius: 999px;
    display: inline-flex;
    flex: 0 0 auto;
    gap: 0.12rem;
    padding: 0.12rem;
  }

  .value_mode_toggle button {
    background: transparent;
    border: 0;
    border-radius: 999px;
    color: var(--pax-black);
    cursor: pointer;
    font-size: 0.68rem;
    line-height: 1;
    padding: 0.22rem 0.38rem;
  }

  .value_mode_toggle button.active {
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .value_mode_toggle button:hover:not(.active) {
    background: var(--pax-smoke);
  }

  .arrow {
    flex: 0 0 auto;
    width: 0.85rem;
  }

  .quant_summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25rem;
    padding: 0.5rem;
  }

  .summary_head,
  .summary_grid {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .summary_head {
    color: #5f6770;
    font-size: 0.75rem;
    gap: 0.5rem;
    padding: 0 0 0.35rem;
    text-align: left;
  }

  .summary_head span {
    padding-left: 0.75rem;
  }

  .summary_grid {
    gap: 0.5rem;
    padding: 0;
  }

  .summary_grid > div {
    border: 1px solid var(--border-color);
    border-radius: 0.4rem;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-height: 4rem;
    min-width: 0;
    justify-content: center;
    padding: 0.6rem 0.75rem;
  }

  .summary_grid small {
    color: #5f6770;
    font-size: 0.75rem;
  }

  .summary_grid strong {
    font-size: 1.05rem;
  }

  .summary_grid .down .metric_value {
    color: #1f8b35;
  }

  .summary_grid .up .metric_value {
    color: #bd2828;
  }

  .after_card .quant_summary {
    grid-template-columns: repeat(auto-fit, minmax(5rem, 1fr));
  }

  .quant_summary div {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.25rem 0.2rem;
    border-radius: 0.35rem;
    text-align: center;
  }

  .quant_summary strong {
    align-items: center;
    display: flex;
    justify-content: center;
    font-size: 0.875rem;
    line-height: 1;
  }

  .metric_value {
    background: var(--pax-white);
    border-radius: 999px;
    display: inline-flex;
    padding: 0.22rem 0.42rem;
  }

  .quant_summary .down .metric_value {
    background: #b7e3bd;
  }

  .quant_summary .up .metric_value {
    background: #e88484;
  }

  .quant_summary span {
    color: #5f6770;
    font-size: 0.75rem;
  }

</style>
